import React, { useState, useRef } from 'react';
import { closeIcon } from '../../utils/images';
import { uploadUserMedia } from '../../services/personalMediaList';
import '../MediaLibraryPopup/MediaLibraryPopup.scss';
import './PersonalMediaUpload.scss';

export const PersonalMediaUpload: React.FC<{
  onClose: () => void;
  onSuccess?: () => void;
}> = ({ onClose, onSuccess }) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [previewErrors, setPreviewErrors] = useState<Record<number, boolean>>({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setPendingFiles(files);
    setPreviewErrors({});
    Promise.all(
      files.map(
        file =>
          new Promise<string>(resolve => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          })
      )
    ).then(urls => setPreviewUrls(urls));
  };

  const handleConfirm = async () => {
    if (pendingFiles.length === 0) return;
    try {
      setUploading(true);
      await uploadUserMedia(pendingFiles);
      onSuccess?.();
      onClose();
    } catch {
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="personal-media-upload-overlay">
      <div className="media-popup">
        <div className="media-popup__header">
          <div className="media-popup__close" onClick={onClose}>
            <img className="media-popup__close--icon" src={closeIcon} alt="close" />
          </div>
          <h1 className="media-popup__title">Add Media</h1>
        </div>

        <div className="media-popup__container">
          <div
            className="media-popup__upload-preview-area"
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            {previewUrls.length > 0 ? (
              <div className="media-popup__upload-preview-grid">
                {previewUrls.map((url, i) => (
                  previewErrors[i] ? (
                    <div key={i} className="media-popup__upload-preview-fallback">
                      <span className="media-popup__upload-preview-filename">{pendingFiles[i]?.name || `Image ${i + 1}`}</span>
                    </div>
                  ) : (
                    <img
                      key={i}
                      src={url}
                      alt={pendingFiles[i]?.name || `Image ${i + 1}`}
                      className="media-popup__upload-preview-thumb"
                      onError={() => setPreviewErrors(prev => ({ ...prev, [i]: true }))}
                    />
                  )
                ))}
              </div>
            ) : (
              <span className="media-popup__upload-placeholder">Click to select images</span>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div className="media-popup__upload-actions">
            <button
              className="media-popup__upload-back-btn"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              className="media-popup__upload-confirm-btn"
              onClick={handleConfirm}
              disabled={pendingFiles.length === 0 || uploading}
            >
              {uploading ? 'Uploading...' : `Confirm${pendingFiles.length > 1 ? ` (${pendingFiles.length})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
